import { useLocale, useTranslate } from '@va/localization';
import { ChartContext, ChartTile, ChartVisualizationComponentProps } from '@va/types/charts';
import { BaseTimeseriesDataset } from '@va/types/time-series';
import { useReportBlockConfig, useReportBlockCtx } from '@va/ui/components/report-block';
import { externalChartTooltip } from '@va/ui/design-system';
import { useRefreshCallbacks } from '@va/util/components';
import { chartHelpers, formatLineDiagramDateLabels } from '@va/util/helpers';
import { useHiddenKPIs } from '@va/util/hooks';
import { ChartData, ChartTypeRegistry } from 'chart.js';
import React, { useCallback, useMemo } from 'react';
import { useChartIndicatorRenderers } from './useChartIndicatorRenderers';

export const useCommonChartViewData = <T extends keyof ChartTypeRegistry>({
  useCurrentData,
  usePreviousData,
  getHeadingValueFunc,
  getValueDetailsFunc,
  tooltipTitle,
  tooltipSubtitle,
  getTooltipValueFunc,
  reversePercentageColors,
  renderTileContextMenu,
  renderTileCta,
  getExtraDatasetConfig,
  tooltipMode,
  renderErrorComponent,
  showPrevious: showPreviousExt,
  disableIndicators,
}: ChartVisualizationComponentProps & {
  renderTileContextMenu?: (key: string) => React.ReactNode;
  renderTileCta?: (key: string) => React.ReactNode;
  getExtraDatasetConfig?: (dataset: BaseTimeseriesDataset<any>) => Record<string, any>;
  showPrevious?: boolean;
  disableIndicators?: boolean;
}) => {
  const current = useCurrentData();
  const previous = usePreviousData();

  const { isManualRefreshing } = useRefreshCallbacks(() => {
    current.mutate();
    previous.mutate();
  });
  const currentData = useMemo(() => current.data, [current.data]);
  const previousData = useMemo(() => previous.data, [previous.data]);

  const isLoading = current.isLoading || previous.isLoading || isManualRefreshing;

  const { id, showPrevious: showPreviousRb } = useReportBlockCtx();
  const { storageNamespace } = useReportBlockConfig();
  const { locale } = useLocale();

  const showPrevious = showPreviousExt ?? showPreviousRb;

  const { hiddenKPIs, toggleKpiVisibility } = useHiddenKPIs({ reportBlockId: id, namespace: storageNamespace });

  const translate = useTranslate();

  const { renderIndicatorLines, renderTooltipHeader } = useChartIndicatorRenderers();

  const tiles: ChartTile[] = useMemo(() => {
    if (!currentData || !previousData) return [];

    return chartHelpers.buildChartTiles({
      currentDatasets: currentData.items,
      currentTotal: currentData.total,
      hiddenDatasets: hiddenKPIs,
      previousTotal: previousData.total,
      locale: locale,
      translate: translate,
      getHeadingValueFunc: getHeadingValueFunc,
      getValueDetailsFunc,
      renderContextMenu: renderTileContextMenu,
      renderCta: renderTileCta,
    });
  }, [
    currentData,
    getHeadingValueFunc,
    getValueDetailsFunc,
    hiddenKPIs,
    locale,
    previousData,
    renderTileContextMenu,
    renderTileCta,
    translate,
  ]);

  const chartData: ChartData<T> = useMemo(() => {
    if (!currentData || !previousData) return { datasets: [], labels: [] };
    return {
      labels: currentData.chartLabels ? formatLineDiagramDateLabels(currentData.chartLabels, locale) : [],
      datasets: chartHelpers.buildChartDatasets({
        currentDatasets: currentData.items,
        previousDatasets: previousData.items,
        hiddenDatasets: hiddenKPIs,
        isPreviousEnabled: showPrevious,
        getExtraConfig: getExtraDatasetConfig,
      }),
    } as ChartData<T>;
  }, [currentData, getExtraDatasetConfig, hiddenKPIs, locale, previousData, showPrevious]);

  const tooltipFn = useCallback(
    (ctx: ChartContext) => {
      return externalChartTooltip(ctx, {
        title: tooltipTitle ?? translate('dashboard.lineChart.tooltip.title'),
        subtitle: tooltipSubtitle ?? translate('dashboard.lineChart.tooltip.subtitle'),
        showPrevious: showPrevious,
        currentTimeIntervals: currentData?.timeIntervals ?? [],
        previousTimeIntervals: previousData?.timeIntervals ?? [],
        valueTransformer: getTooltipValueFunc,
        reversePercentageColors: reversePercentageColors,
        tooltipMode: tooltipMode,
        reportBlockId: id,
        renderIndicatorLines: disableIndicators ? undefined : renderIndicatorLines,
        renderTooltipHeader: disableIndicators ? undefined : renderTooltipHeader,
      });
    },
    [
      currentData?.timeIntervals,
      disableIndicators,
      getTooltipValueFunc,
      id,
      previousData?.timeIntervals,
      renderIndicatorLines,
      renderTooltipHeader,
      reversePercentageColors,
      showPrevious,
      tooltipMode,
      tooltipSubtitle,
      tooltipTitle,
      translate,
    ],
  );

  const errorComponent = useMemo(() => {
    return renderErrorComponent
      ? renderErrorComponent(current.error ?? previous.error ?? undefined, isLoading)
      : undefined;
  }, [current.error, isLoading, previous.error, renderErrorComponent]);

  return { tooltipFn, chartData, tiles, toggleKpiVisibility, isLoading, errorComponent };
};
